// Google Analytics 4 (GA4) — SPA-safe initialization + typed event helpers.
//
// Enhanced Measurement events (session_start, user_engagement, scroll, outbound
// link clicks, file downloads, form interactions) are handled automatically by
// GA4 when "Enhanced measurement" is enabled on the Data Stream. This module
// only handles: (1) one-time script load, (2) route-change page_view firing
// (we disable auto page_view to prevent dupes), (3) typed custom-event helpers.

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const DEBUG = process.env.REACT_APP_GA_DEBUG === "true";

let _initialized = false;

/** Call gtag using the exact Google-recommended pattern: push the `arguments`
 * object (not an Array) so gtag.js recognizes and processes each command. */
function gtag() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

// Expose a global `gtag` shim identical to Google's snippet, in case any
// third-party integration (e.g. reCAPTCHA) needs to call it.
if (typeof window !== "undefined" && !window.gtag) {
  window.gtag = gtag;
}

/** Load gtag.js exactly once and configure the measurement ID. */
export function initGA() {
  if (_initialized) return true;
  if (!MEASUREMENT_ID) {
    if (DEBUG) console.warn("[GA4] REACT_APP_GA_MEASUREMENT_ID is not set — analytics disabled.");
    return false;
  }
  if (typeof document === "undefined") return false;

  // Guard against re-injection if another loader added it (e.g. hot reload).
  if (!document.getElementById("ga4-src")) {
    const s = document.createElement("script");
    s.async = true;
    s.id = "ga4-src";
    s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(s);
  }

  gtag("js", new Date());
  // We disable gtag's automatic page_view so React Router can emit one per
  // route change (including the initial mount) — that gives us clean SPA
  // tracking without duplicates.
  gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    debug_mode: DEBUG || undefined,
  });

  _initialized = true;
  if (DEBUG) console.info("[GA4] initialized with", MEASUREMENT_ID);
  return true;
}

/** Fire a page_view. Call on every SPA route change. */
export function trackPageView(path, title) {
  if (!MEASUREMENT_ID) return;
  gtag("event", "page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.origin + path : path,
    page_title: title || (typeof document !== "undefined" ? document.title : undefined),
  });
}

/** Fire an arbitrary GA4 event. */
export function trackEvent(name, params = {}) {
  if (!MEASUREMENT_ID) return;
  gtag("event", name, params);
  if (DEBUG) console.info("[GA4] event", name, params);
}

/** Associate a user_id with subsequent hits (called on login / register). */
export function setUserId(userId) {
  if (!MEASUREMENT_ID || !userId) return;
  gtag("config", MEASUREMENT_ID, { user_id: userId, send_page_view: false });
}

/** Clear the user id on logout. */
export function clearUserId() {
  if (!MEASUREMENT_ID) return;
  gtag("config", MEASUREMENT_ID, { user_id: null, send_page_view: false });
}

/* ----------------------- Typed custom events ----------------------- */
export const ga = {
  signUp:            (method = "email")     => trackEvent("sign_up",  { method }),
  login:             (method = "email")     => trackEvent("login",    { method }),
  stockSearch:       (query)                => trackEvent("stock_search",              { search_term: (query || "").slice(0, 64) }),
  stockAnalysis:     (symbol, cached=false) => trackEvent("stock_analysis_generated",  { symbol, cached }),
  upgradeClick:      (source = "unknown")   => trackEvent("upgrade_click",             { source }),
  paymentSuccess:    (params = {})          => trackEvent("payment_successful",        params),
};
